// src/hooks/useNotifications.js

import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import { useUser } from '../contexts/UserContext';
import { LocalNotificationService } from '../services/notifications/LocalNotificationService';
import { FCMService } from '../services/notifications/FCMService';
import { LocalNotifications } from '@capacitor/local-notifications';
import { clubesHabilitado } from '../features/clubes/clubesHabilitado';
import { listarMeusClubes } from '../features/clubes/clubesApi';
import { abrirTelaGlobal } from '../utils/abrirTelaGlobal';

// Chave de persistência do último reagendamento (epoch ms).
const LAST_SCHEDULE_KEY = 'lesvita_notif_last_scheduled';
// Só reagenda se passou esse tempo desde a última vez (evita reagendar várias
// vezes no mesmo dia). Exceção: quando a fila de pendentes está baixa.
const MIN_HOURS_BETWEEN  = 20;
// Se há menos que isso de notificações pendentes, reagenda imediatamente
// (a fila secou — caso típico depois que o iOS deixou de re-disparar).
const MIN_PENDING_FLOOR  = 3;

// Convite de clube por notificação: 2 por semana, enquanto a pessoa não
// estiver em nenhum clube — inclusive quem está numa competição de academia
// (competição de academia não é clube). Sem teto de semanas (Tadeu, 07/10).
const chaveConviteClube = (uid) => `lesvita:notif-clube:${uid}`;

function semanaAtual() {
  const d = new Date();
  const inicioAno = new Date(d.getFullYear(), 0, 1);
  const semana = Math.ceil(((d - inicioAno) / 86400000 + inicioAno.getDay() + 1) / 7);
  return `${d.getFullYear()}-S${semana}`;
}

/**
 * Agenda (ou não) o convite de clube desta semana. As datas da semana ficam
 * fixas mesmo com o reagendamento a cada ~20h apagando e refazendo a fila.
 */
async function agendarConviteDeClube(uid) {
  if (!uid) return;
  try {
    const atual = semanaAtual();
    if (!(await clubesHabilitado(uid))) return;
    const meus = await listarMeusClubes();
    if ((meus?.itens || []).length > 0) return; // já joga: convite não faz sentido

    // Datas da semana, fixadas na primeira vez: daqui a 2 dias às 19h30 e
    // daqui a 5 às 12h30. Os reagendamentos seguintes reusam as mesmas.
    const chaveDatas = `${chaveConviteClube(uid)}:datas`;
    let fixadas = null;
    try { fixadas = JSON.parse(localStorage.getItem(chaveDatas) || 'null'); } catch { fixadas = null; }
    if (!fixadas || fixadas.semana !== atual) {
      const em = (dias, hora, minuto) => {
        const d = new Date();
        d.setDate(d.getDate() + dias);
        d.setHours(hora, minuto, 0, 0);
        return d.toISOString();
      };
      fixadas = { semana: atual, datas: [em(2, 19, 30), em(5, 12, 30)] };
      localStorage.setItem(chaveDatas, JSON.stringify(fixadas));
    }
    await LocalNotificationService.scheduleClubInvite(fixadas.datas);
  } catch (e) {
    console.warn('[useNotifications] convite de clube:', e?.message);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Hora mais comum num array de timestamps (ms) */
function mostCommonHour(timestamps) {
  if (!timestamps?.length) return null;
  const counts = {};
  timestamps.forEach(ts => {
    const h = new Date(ts).getHours();
    counts[h] = (counts[h] || 0) + 1;
  });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted.length ? parseInt(sorted[0][0]) : null;
}

/** Extrai timestamps de uma lista de documentos Firestore */
function extractTimestamps(docs, field = 'CREATED_AT') {
  return docs
    .map(d => {
      const val = d[field];
      if (!val) return null;
      if (typeof val.toDate === 'function') return val.toDate().getTime();
      if (typeof val._seconds === 'number') return val._seconds * 1000;
      if (typeof val === 'number') return val;
      return null;
    })
    .filter(Boolean);
}

/** Dias desde a última data ISO (YYYY-MM-DD) */
function daysSince(dateStr) {
  if (!dateStr) return 999;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / 86_400_000);
}

/** Converte um valor de horário de treino em hora numérica (0-23).
 *  Aceita "10:24" => 10, "10" => 10, número => número, e termos PT-BR. */
function parseWorkoutHour(raw) {
  if (raw == null) return null;

  if (typeof raw === 'number') {
    return raw >= 0 && raw <= 23 ? raw : null;
  }

  if (typeof raw !== 'string') return null;

  const value = raw.trim().toLowerCase();

  // Formato "HH:MM" ou apenas dígitos
  const match = value.match(/(\d{1,2})/);
  if (match) {
    const hour = parseInt(match[1], 10);
    return hour >= 0 && hour <= 23 ? hour : null;
  }

  if (value.includes('manhã') || value.includes('manha')) return 7;
  if (value.includes('tarde')) return 16;
  if (value.includes('noite')) return 19;

  return null;
}

/** Converte qualquer formato de data Firestore/string/number em ms (para ordenar) */
function toMillis(date) {
  if (!date) return 0;
  if (typeof date.toDate === 'function') return date.toDate().getTime();
  if (typeof date._seconds === 'number') return date._seconds * 1000;
  if (typeof date === 'string') return new Date(date).getTime();
  if (typeof date === 'number') return date;
  return 0;
}

/**
 * Busca o horário de treino no caminho correto:
 *   PACIENTE/{uid}/OBJETIVO/{docMaisRecente}/SCHEDULE.EXERCISETIME
 *
 * Retorna a hora numérica (0-23) ou null se não encontrar.
 */
async function detectWorkoutHourFromObjetivo(uid) {
  if (!uid) return null;

  try {
    const db = getFirestore();
    const objetivoRef = collection(db, 'PACIENTE', uid, 'OBJETIVO');
    const snap = await getDocs(objetivoRef);

    if (snap.empty) {
      // console.log('[useNotifications] Nenhum OBJETIVO encontrado para o paciente:', uid);
      return null;
    }

    const objetivos = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Documento mais recente pelo campo CREATE_AT (com fallbacks defensivos)
    const objetivoAtual = objetivos.sort((a, b) => {
      const ta = toMillis(a.CREATE_AT ?? a.CREATED_AT ?? a.DATA_CRIACAO ?? a.createdAt);
      const tb = toMillis(b.CREATE_AT ?? b.CREATED_AT ?? b.DATA_CRIACAO ?? b.createdAt);
      return tb - ta;
    })[0];

    const raw = objetivoAtual?.SCHEDULE?.EXERCISETIME;

    // console.log('[useNotifications] Objetivo mais recente:', objetivoAtual?.id);
    // console.log('[useNotifications] SCHEDULE.EXERCISETIME:', raw);

    return parseWorkoutHour(raw);
  } catch (error) {
    console.warn('[useNotifications] Erro ao buscar OBJETIVO:', error);
    return null;
  }
}

// ── Hook principal ────────────────────────────────────────────────────────────
const useNotifications = () => {
  const { user, profileData, vitaData, mealScans } = useUser();
  const initializedRef  = useRef(false);
  const prevStreakRef   = useRef(null);
  const prevXpRef       = useRef(null);
  const reschedulingRef = useRef(false); // evita reagendamentos concorrentes

  // ── scheduleAll: agenda todas as notificações do zero ─────────────────────
  const scheduleAll = async () => {

    if (Capacitor.getPlatform() === 'android') {
      await LocalNotifications.createChannel({
        id:          'lesvita_main',
        name:        'LesVita',
        description: 'Lembretes de treino, refeições e ranking',
        importance:  4,
        visibility:  1,
        vibration:   true,
        sound:       'default',
      });
    }

    // ── Declara tudo primeiro ──────────────────────────────────
    const xpVita       = vitaData?.XP_VITA;
    const streakDays   = xpVita?.STREAK_DIAS      ?? 0;
    const currentXp    = xpVita?.CURRENT_LEVEL_XP ?? 0;
    const nextXp       = xpVita?.NEXT_LEVEL_XP    ?? 100;
    const level        = xpVita?.LEVEL             ?? 1;
    const uid          = user?.id || profileData?.USER_ID;
    const { lunchHour, dinnerHour } = detectMealHours(mealScans);
    const lastCheckinDate  = vitaData?.LAST_TREINO_DATE ?? null;
    const daysSinceWorkout = daysSince(lastCheckinDate);

    // ── 1. Streak ──────────────────────────────────────────────
    await LocalNotificationService.scheduleStreakRisk(streakDays);

    // ── 2. Treino ──────────────────────────────────────────────
    // Horário vem APENAS de PACIENTE/{uid}/OBJETIVO/{docMaisRecente}/SCHEDULE.EXERCISETIME
    const workoutHour = await detectWorkoutHourFromObjetivo(uid);

    if (workoutHour == null) {
      console.warn(
        '[useNotifications] EXERCISETIME não encontrado em OBJETIVO — notificação de treino NÃO será agendada.'
      );
    } else {
      const workoutNotifOptions = ['before', 'checkin', 'planDay'];
      const chosen = workoutNotifOptions[Math.floor(Math.random() * workoutNotifOptions.length)];

      // console.log('[useNotifications] Notificação de treino sorteada:', chosen, '| hora:', workoutHour);

      if (chosen === 'before')   await LocalNotificationService.scheduleWorkoutBefore(workoutHour);
      if (chosen === 'checkin')  await LocalNotificationService.scheduleWorkoutCheckin(workoutHour);
      if (chosen === 'planDay')  await LocalNotificationService.scheduleWorkoutPlanDay(workoutHour);
    }

    // ── 3. Sem atividade ───────────────────────────────────────
    await LocalNotificationService.scheduleNoActivity(daysSinceWorkout);

    // ── 4. Refeições ───────────────────────────────────────────
    await LocalNotificationService.scheduleMealReminders({ lunchHour, dinnerHour });

    // ── 5. XP próximo ao level up ──────────────────────────────
    const xpNeeded = nextXp - currentXp;
    await LocalNotificationService.scheduleXpLevelUp(xpNeeded, level + 1);

    // ── 6. Convite para criar um clube e jogar com amigos/família ─────
    await agendarConviteDeClube(uid);
  };

  // ── Reagendamento guardado ────────────────────────────────────────────────
  // Limpa tudo e reagenda os próximos 7 dias, MAS só quando faz sentido:
  //   • passou >= MIN_HOURS_BETWEEN desde a última vez, OU
  //   • a fila de pendentes secou (< MIN_PENDING_FLOOR).
  // Garante cancelAll() concluído ANTES de scheduleAll() (await encadeado).
  const maybeReschedule = async ({ force = false } = {}) => {
    if (!Capacitor.isNativePlatform()) return;
    if (reschedulingRef.current) return; // já tem um reagendamento em curso

    const pending    = await LocalNotificationService.getPendingCount();
    const last       = Number(localStorage.getItem(LAST_SCHEDULE_KEY) || 0);
    const hoursSince = (Date.now() - last) / 3_600_000;

    const shouldRun = force || pending < MIN_PENDING_FLOOR || hoursSince >= MIN_HOURS_BETWEEN;
    if (!shouldRun) return;

    reschedulingRef.current = true;
    try {
      await LocalNotificationService.cancelAll(); // ← aguarda limpeza completa
      await scheduleAll();                         // ← só então reagenda
      localStorage.setItem(LAST_SCHEDULE_KEY, String(Date.now()));
    } finally {
      reschedulingRef.current = false;
    }
  };

  // ── Efeito principal: inicializa quando usuário carrega ───────────────────
  useEffect(() => {
    if (!user?.id || !profileData || !vitaData) return;
    if (!Capacitor.isNativePlatform()) return;
    if (initializedRef.current) return;

    const init = async () => {
      // Camada de push: anexa SEM pedir permissão. O prompt do SO acontece num
      // MOMENTO DE VALOR (treino salvo, avaliação registrada — ver PUSH_PROMPT
      // no FCMService), não no primeiro segundo do boot. Se a permissão já
      // existe (sessão anterior), o attach re-registra o token em silêncio.
      await FCMService.attach(user.id || profileData.USER_ID);

      // Lembretes locais usam a MESMA permissão do SO. Aqui só agenda se ela
      // JÁ existe; quem pede é o momento de valor. Quando ela for concedida
      // lá, o evento abaixo ('permission-granted') reagenda na hora.
      const granted = await LocalNotificationService.hasPermission();
      if (!granted) return;
      // Guardado: na primeira vez (sem timestamp) sempre reagenda; depois respeita
      // o intervalo de 20h / piso de pendentes.
      await maybeReschedule();
      initializedRef.current = true;
    };

    init().catch(console.error);
  }, [user?.id, !!profileData, !!vitaData]);

  // ── Toque numa notificação local → abre a tela que ela promete ───────────
  // Antes nenhuma notificação local tinha destino: o toque só abria o app.
  // `extra.tela` (ex.: 'cl:criar') vai para o shell; com o app fechado, o
  // pedido fica guardado até a plataforma montar (abrirTelaGlobal persistir).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let handle;
    (async () => {
      handle = await LocalNotifications.addListener(
        'localNotificationActionPerformed',
        (acao) => {
          const extra = acao?.notification?.extra || {};
          if (extra.tela) abrirTelaGlobal(extra.tela, extra.dados || null, { persistir: true });
        }
      );
    })();
    return () => { handle?.remove(); };
  }, []);

  // ── Permissão concedida num momento de valor → agenda os lembretes locais ─
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const onGranted = () => {
      maybeReschedule().then(() => { initializedRef.current = true; })
        .catch(console.error);
    };
    window.addEventListener('lesvita:push-permission-granted', onGranted);
    return () => window.removeEventListener('lesvita:push-permission-granted', onGranted);
    // Deps vazias de propósito: o listener é registrado UMA vez por montagem e
    // lê tudo por referência estável (maybeReschedule/initializedRef).
  }, []);

  // ── Reagendamento ao trazer o app para foreground ─────────────────────────
  // Cobre o caso central do bug: depois de ~1 semana o iOS para de re-disparar
  // as recorrentes. A cada abertura verificamos a fila e reabastecemos.
  useEffect(() => {
    if (!user?.id || !profileData || !vitaData) return;
    if (!Capacitor.isNativePlatform()) return;

    let handle;
    (async () => {
      handle = await App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) return;
        // maybeReschedule é auto-guardado (20h / piso de pendentes), então é
        // seguro chamar a cada foreground sem depender do init ter concluído.
        maybeReschedule().catch(console.error);
      });
    })();

    return () => { handle?.remove(); };
  }, [user?.id, !!profileData, !!vitaData]);

  // ── Reage a mudanças de streak ────────────────────────────────────────────
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const streak = vitaData?.XP_VITA?.STREAK_DIAS;
    if (streak == null) return;

    // Milestone de streak
    const MILESTONES = [7, 14, 30, 60, 100];
    if (MILESTONES.includes(streak) && prevStreakRef.current !== streak) {
      LocalNotificationService.scheduleStreakMilestone(streak);
    }

    // Atualiza o risco de streak (recancel + reagenda com dia correto).
    // cancelByBase remove todas as variantes diárias (1001*10+0..6).
    if (streak > 0 && prevStreakRef.current !== streak) {
      (async () => {
        await LocalNotificationService.cancelByBase([1001]);
        await LocalNotificationService.scheduleStreakRisk(streak);
      })();
    }

    prevStreakRef.current = streak;
  }, [vitaData?.XP_VITA?.STREAK_DIAS]);

  // ── Reage a mudanças de XP (perto de level up) ────────────────────────────
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const xpVita = vitaData?.XP_VITA;
    if (!xpVita) return;

    const xpNeeded  = (xpVita.NEXT_LEVEL_XP ?? 100) - (xpVita.CURRENT_LEVEL_XP ?? 0);
    const nextLevel = (xpVita.LEVEL ?? 1) + 1;

    if (xpNeeded !== prevXpRef.current) {
      LocalNotificationService.cancelById([1011]);
      LocalNotificationService.scheduleXpLevelUp(xpNeeded, nextLevel);
      prevXpRef.current = xpNeeded;
    }
  }, [vitaData?.XP_VITA?.CURRENT_LEVEL_XP]);

};

// ── Detectores inteligentes ───────────────────────────────────────────────────

/** Detecta horas de refeição pelo histórico de mealScans */
function detectMealHours(mealScans) {
  const defaults = { lunchHour: 12, dinnerHour: 19 };
  if (!mealScans?.length || mealScans.length < 5) return defaults;

  const timestamps = extractTimestamps(mealScans, 'CREATED_AT');

  const lunchTs  = timestamps.filter(ts => { const h = new Date(ts).getHours(); return h >= 10 && h <= 14; });
  const dinnerTs = timestamps.filter(ts => { const h = new Date(ts).getHours(); return h >= 17 && h <= 21; });

  return {
    lunchHour:  lunchTs.length  >= 3 ? (mostCommonHour(lunchTs)  ?? 12) : 12,
    dinnerHour: dinnerTs.length >= 3 ? (mostCommonHour(dinnerTs) ?? 19) : 19,
  };
}

export default useNotifications;